import { Lock, MessageCircle } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import OtpVerificationCard from '@/domains/auth/components/OtpVerificationCard/OtpVerificationCard';
import usePhoneVerification from '@/domains/auth/hooks/usePhoneVerification';
import { usePendingAuthStore } from '@/domains/auth/store/pendingAuthStore';
import { apiErrorMessage } from '@/domains/auth/utils/apiError';
import { NEW_PASSWORD_ROUTE } from '@/domains/auth/utils/authRoutes';
import { normalizePhone } from '@/domains/auth/utils/phone';
import InputMobile from '@/domains/mobile-web/components/InputMobile/InputMobile';
import TopBarHeader from '@/domains/mobile-web/components/TopBarHeader/TopBarHeader';
import WrapperMobile from '@/shared/components/layout/WrapperMobile/WrapperMobile';
import Button from '@/shared/components/ui/Button';
import HeaderMobile from '@/shared/components/ui/HeaderMobile/HeaderMobile';
import { LOGIN_ROUTE } from '@/shared/utils/loginRedirect';

const LOCK_ICON = <Lock className="size-8 text-black" aria-hidden="true" />;

// Lupa Password: the registered phone number, then the WhatsApp (or SMS) code.
// A matching code verifies the number in the server session for Password Baru.
const ForgotPassword = () => {
  const navigate = useNavigate();
  const { turnstileRef, sent, send, verify, resendIn, isSending, isVerifying, error, setError } =
    usePhoneVerification();
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [code, setCode] = useState('');
  // The code step is shown once a code for a password reset went out
  const [showCode, setShowCode] = useState(false);
  const resetSent = showCode && sent?.purpose === 'reset' ? sent : null;

  const onSendCode = async (event: FormEvent) => {
    event.preventDefault();
    const phone = normalizePhone(phoneInput);
    if (!phone) {
      setPhoneError('Nomor HP tidak valid. Contoh: 085776235639.');
      return;
    }
    setPhoneError('');
    try {
      await send({ purpose: 'reset', phone_number: phone });
      setCode('');
      setShowCode(true);
    } catch (err) {
      setPhoneError(apiErrorMessage(err, 'Kode tidak dapat dikirim. Silakan coba lagi.'));
    }
  };

  const onResend = async () => {
    if (!resetSent) return;
    try {
      await send({ purpose: 'reset', phone_number: resetSent.phone });
      setCode('');
    } catch (err) {
      setError(apiErrorMessage(err, 'Kode tidak dapat dikirim. Silakan coba lagi.'));
    }
  };

  const onVerify = async () => {
    if (!(await verify(code))) return;
    usePendingAuthStore.getState().setResetVerified(true);
    navigate(NEW_PASSWORD_ROUTE, { replace: true });
  };

  const onBack = () => {
    if (resetSent) {
      setShowCode(false);
      setCode('');
      setError('');
      return;
    }
    navigate(LOGIN_ROUTE);
  };

  // One widget host for both steps, so Turnstile is not rendered twice
  const turnstileHost = <div ref={turnstileRef} className="flex justify-center empty:hidden" />;

  return (
    <main className="h-screen w-full overflow-hidden justify-between bg-[linear-gradient(180deg,#FFF_0%,#007AFF_250.56%)]">
      <HeaderMobile className="bg-transparent!">
        <TopBarHeader type="back" title="Verifikasi Nomor" onBack={onBack} />
      </HeaderMobile>

      <WrapperMobile className="flex-1 overflow-auto m-auto h-full">
        {resetSent ? (
          <OtpVerificationCard
            icon={LOCK_ICON}
            title="Verifikasi Kode"
            phone={resetSent.phone}
            channel={resetSent.channel}
            code={code}
            onCodeChange={setCode}
            onSubmit={onVerify}
            onResend={onResend}
            resendIn={resendIn}
            error={error}
            isSending={isSending}
            isSubmitting={isVerifying}
          />
        ) : (
          <form
            onSubmit={onSendCode}
            noValidate
            className="shadowCard h-fit p-6 flex-col flex gap-6 rounded-[24px] mt-[90px] sm:m-auto md:m-auto lg:m-auto mb-[50px]"
          >
            <div className="flex flex-col items-center gap-2 text-center">
              <MessageCircle className="size-8 text-black" aria-hidden="true" />
              <p className="title2medium text-black">Verifikasi Nomor</p>
              <p className="reg12 text-text-grey">
                Masukkan nomor aktif anda saat ini untuk menerima kode verifikasi.
              </p>
            </div>

            <InputMobile
              label="Nomor HP"
              name="phone"
              type="tel"
              inputMode="numeric"
              placeholder="Contoh: 085776235639"
              autoComplete="tel"
              value={phoneInput}
              onChange={(event) => setPhoneInput(event.target.value)}
              error={phoneError}
              helperText="Pastikan nomor anda aktif dan dapat menerima pesan. "
            />

            <Button
              type="submit"
              customButton="btn-fill-blue"
              disabled={!phoneInput.trim() || isSending}
            >
              <span className="title2medium text-white">
                {isSending ? 'Memproses...' : 'Kirim kode Verifikasi'}
              </span>
            </Button>
          </form>
        )}
        {turnstileHost}
      </WrapperMobile>
    </main>
  );
};

export default ForgotPassword;
